import React, { useEffect, useMemo, useRef, useState } from 'react';

const MAX_VISIBLE_OPTIONS = 60;

function getUserLabel(u) {
  const firstName = u.firstName ?? u.FirstName ?? '';
  const lastName = u.lastName ?? u.LastName ?? '';
  const userName = u.userName ?? u.UserName ?? '';
  const name = `${firstName} ${lastName}`.trim();
  return name || userName || `User ${u.userId ?? u.UserId}`;
}

function getUserId(u) {
  return u.userId ?? u.UserId;
}

export default function SearchableUserIdSelect({
  users = [],
  value = '',
  onChange,
  allLabel = 'All Users',
  searchPlaceholder = 'Search user...',
  className = '',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  const selectedUser = users.find((u) => String(getUserId(u)) === String(value));
  const displayLabel = selectedUser ? getUserLabel(selectedUser) : allLabel;

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) =>
      getUserLabel(a).localeCompare(getUserLabel(b), undefined, { sensitivity: 'base' })
    ),
    [users]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedUsers;
    return sortedUsers.filter((u) => {
      const label = getUserLabel(u).toLowerCase();
      const userName = String(u.userName ?? u.UserName ?? '').toLowerCase();
      return label.includes(q) || userName.includes(q);
    });
  }, [sortedUsers, search]);

  const visibleOptions = useMemo(
    () => filtered.slice(0, MAX_VISIBLE_OPTIONS),
    [filtered]
  );

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleSelect = (userId) => {
    onChange(userId);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className={`searchable-id-select ${className}`.trim()} ref={containerRef}>
      <button
        type="button"
        className="searchable-id-select-trigger form-select"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="searchable-id-select-trigger-label">{displayLabel}</span>
      </button>
      {open && (
        <div className="searchable-id-select-dropdown">
          <div className="searchable-id-select-search">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
              autoFocus
            />
          </div>
          <div className="searchable-id-select-list" role="listbox">
            <button
              type="button"
              className={`searchable-id-select-option${!value ? ' active' : ''}`}
              onClick={() => handleSelect('')}
            >
              {allLabel}
            </button>
            {filtered.length === 0 ? (
              <div className="searchable-id-select-empty">No users found</div>
            ) : (
              <>
                {visibleOptions.map((u) => (
                  <button
                    key={getUserId(u)}
                    type="button"
                    className={`searchable-id-select-option${String(value) === String(getUserId(u)) ? ' active' : ''}`}
                    onClick={() => handleSelect(String(getUserId(u)))}
                  >
                    {getUserLabel(u)}
                  </button>
                ))}
                {filtered.length > MAX_VISIBLE_OPTIONS && (
                  <div className="searchable-id-select-empty">
                    Showing {MAX_VISIBLE_OPTIONS} of {filtered.length}. Type more to narrow results.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
